'use client';

import Image from 'next/image';

import type { JSX } from 'react';

import { cn, wrapArray } from '~/utils';
import { Text } from '~/components/text';
import { Show } from '~/components/show';
import { useUpload } from '~/hooks/useUpload';
import { Progress } from '~/components/progress';
import { HStack, Stack } from '~/components/stack';
import { Avatar, AvatarImage } from '~/components/avatar';
import {
  PdfIcon,
  CameraIcon,
  UploadIcon,
  XCircleIcon,
} from '~/components/icons';

import type { IFileProps, TAccept } from '~/types';

interface IAvatarUploaderProps {
  value?: IFileProps;
  maximumSize?: number;
  accept?: TAccept | TAccept[];
  onChange?: (file: IFileProps) => void;
  onUploadComplete?: (file: IFileProps) => void;
}

export function AvatarUploader({
  value,
  onChange,
  accept = [],
  maximumSize,
  onUploadComplete,
}: IAvatarUploaderProps): JSX.Element {
  accept = wrapArray(accept);

  const {
    files,
    inputRef,
    onDragEnter,
    onDragLeave,
    onChange: handleChange,
  } = useUpload({
    value,
    accept,
    onChange,
    maximumSize,
    isMulti: false,
    onUploadComplete,
  });

  const file = files[0];

  return (
    <div
      className={cn(
        'size-25 flex items-center justify-center rounded-full relative',
        'bg-[#f2f3f5]',
      )}
    >
      <Show when={!file?.id}>
        <CameraIcon className={cn('absolute', file?.id && 'relative')} />
      </Show>
      <Show when={file && file.done === false}>
        <Progress value={file?.progress} />
      </Show>
      <Show when={file && file.done !== false}>
        <Avatar className='w-full h-full'>
          <AvatarImage src={file?.url} />
        </Avatar>
      </Show>
      <input
        type='file'
        ref={inputRef}
        multiple={false}
        onChange={handleChange}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        accept={accept.join(', ')}
        className='absolute inset-0 opacity-0 cursor-pointer'
      />
    </div>
  );
}

interface IUploaderProps extends Omit<IAvatarUploaderProps, 'onChange'> {
  label?: string;
  isMulti?: boolean;
  description?: string;
  onChange?: (signedIds: IFileProps | IFileProps[]) => void;
}

export function Uploader({
  value,
  onChange,
  accept = [],
  maximumSize,
  description,
  isMulti = false,
  onUploadComplete,
  label = 'Drag file or click to upload',
}: IUploaderProps): JSX.Element {
  accept = wrapArray(accept);

  const {
    files,
    remove,
    inputRef,
    isDragging,
    onDragEnter,
    onDragLeave,
    onChange: handleChange,
  } = useUpload({
    value,
    accept,
    isMulti,
    onChange,
    maximumSize,
    onUploadComplete,
  });

  return (
    <Stack gap={4}>
      <Show when={description}>
        <Text size='sm' className='text-dimGray-500'>
          {description}
        </Text>
      </Show>
      <Stack
        gap={2}
        className={cn(
          'px-4 py-3 rounded-md min-h-[84px] items-center justify-center relative',
          'border border-[#f2f3f5] bg-[#f2f3f5]',
          'hover:bg-[#f6f7f8] hover:border-[#f6f7f8] active:border-blueBonnet-500',
          isDragging && 'border-blueBonnet-500',
        )}
      >
        <UploadIcon />
        <Text size='sm'>{label}</Text>
        <input
          type='file'
          ref={inputRef}
          multiple={isMulti}
          onChange={handleChange}
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          accept={accept.join(', ')}
          className='absolute inset-0 opacity-0 cursor-pointer'
        />
      </Stack>
      <HStack gap={5} className='flex-wrap'>
        {files.map((file) => {
          const isPDF = file.contentType === 'application/pdf';
          return (
            <div
              key={file.signedId || file.id}
              className={
                'size-20 rounded-md bg-[#f2f3f5] relative flex items-center justify-center'
              }
            >
              <Show when={file.done !== false}>
                <Show when={!isPDF}>
                  <Image
                    width={80}
                    height={80}
                    alt='image'
                    src={file.url || ''}
                    className='rounded-md size-20 object-cover'
                  />
                </Show>
                <Show when={isPDF}>
                  <PdfIcon className='w-full h-full' />
                </Show>
                <span
                  role='button'
                  className='cursor-pointer'
                  onClick={() => {
                    remove(file);
                  }}
                >
                  <XCircleIcon className='absolute -right-2 -top-3 stroke-desire-500' />
                </span>
              </Show>
              <Show when={file.done === false}>
                <Progress
                  value={file.progress}
                  className='absolute top-1/2 -translate-y-1/2 w-[80%] left-1/2 -translate-x-1/2'
                />
              </Show>
            </div>
          );
        })}
      </HStack>
    </Stack>
  );
}
